import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import usePresence from "../hooks/usePresence";
import { buildPresenceRows, formatElapsedLabel, presenceKey } from "../util/scribePresence";

// A plain ticking clock (mirrors announcer-fe's OrderOfGo.jsx useNow), purely
// a re-render trigger so elapsed-time cells (and the quiet-threshold
// crossover, AC6) advance on screen between network polls rather than
// freezing at whatever value was true when the last poll landed.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;

// #2365 (scribe-presence workstream, card D) — "Is anyone scribing ring 3
// right now? The C sheet hasn't moved in ten minutes, is that scribe gone or
// just slow? We're about to start a class, are all the seats covered?" One
// real table (AC4), one row per (ring, seat) the current class actually
// stages (AC1/AC2), showing who is connected and a real elapsed time next to
// it (AC3) rather than a bare verdict — colour is layered ON TOP of that
// number (a red Alert chip past SCRIBE_QUIET_THRESHOLD_MS, AC6), never
// instead of it. A seat nobody has EVER connected to (never observed by this
// dashboard) reads as a plain, non-alarming "Not connected" (AC2) — the
// card's contract warning is that GET /presence itself cannot tell "just
// went quiet" apart from "never connected" once a row ages past its 60s TTL,
// so this component never claims to either: only seats usePresence has
// personally observed a heartbeat for get an elapsed clock and a quiet
// alarm; everything else stays neutral.
//
// showId/ring/classId/seat on a presence row are CLIENT claims, not
// authorization facts (#2363) — this table reads as "who says they are
// connected here", never as "the system confirms this scribe is assigned".
// #2365 — `ringState` is ShowDashboard's single useRingStates(showId) call,
// shared with RingStateSection so the classes/rides/ring-state fetch happens
// once per mount, not once per section (a second, independent instance here
// doubled that request set on every dashboard load).
export default function ScribePresenceSection({ showId, ringState }) {
  const { t } = useTranslation("scoreadmin");
  const { rows: ringRows, loading: ringLoading, error: ringError } = ringState;
  const { observedByKey, loading: presenceLoading, error: presenceError } = usePresence(showId);
  const now = useNow(CLOCK_TICK_MS);

  if (!showId) return null;

  const loading = ringLoading || presenceLoading;
  const error = ringError || presenceError;
  const rows = buildPresenceRows({ ringRows, observedByKey, now });

  return (
    <Card variant="outlined" data-testid="scribe-presence-section">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("scribePresence.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="scribe-presence-load-error">
            {t("scribePresence.loadError")}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t("scribePresence.noSeats")}</Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="scribe-presence-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("scribePresence.columns.ring")}</TableCell>
                  <TableCell>{t("scribePresence.columns.seat")}</TableCell>
                  <TableCell>{t("scribePresence.columns.scribe")}</TableCell>
                  <TableCell>{t("scribePresence.columns.lastSeen")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={presenceKey(row)}
                    data-testid={`scribe-presence-row-${row.ring}-${row.seat}`}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {row.ring}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        {row.seat}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.scribeName ? (
                        <Typography variant="body2" color="text.primary">
                          {row.scribeName}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`scribe-presence-not-connected-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.notConnected")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {!row.everConnected ? (
                        <Typography variant="body2" color="text.secondary">
                          {"—"}
                        </Typography>
                      ) : row.quiet ? (
                        <Alert
                          severity="error"
                          icon={false}
                          sx={{ py: 0, px: 1, display: "inline-flex" }}
                          data-testid={`scribe-presence-quiet-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.lastSeen", { time: formatElapsedLabel(row.elapsedMs) })}
                        </Alert>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          data-testid={`scribe-presence-lastseen-${row.ring}-${row.seat}`}
                        >
                          {t("scribePresence.lastSeen", { time: formatElapsedLabel(row.elapsedMs) })}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
